import React, { memo } from 'react'

import { MfaMethodList } from '@surglogs-app/auth-mfa'
import { useOnDialogBlur } from '@surglogs-app/hooks'
import { useModal } from '@surglogs-app/prompt'
import { Config } from '@surglogs-app/types'
import { Button } from '@surglogs-app/ui'
import {
  DefaultModalButtons,
  DefaultModalContent,
} from '@surglogs-app/ui.modal'

import { useMfaRevoke } from '../hooks/useMfaRevoke'
import { useSecurityPin } from '../hooks/useSecurityPin'
import { useAccountSettingsTranslation } from '../services'

import { AccountEditPasswordModal } from './AccountEditPasswordModal'
import { AccountEditPinModal } from './AccountEditPinModal'
import { SecurityActionItem } from './SecurityActionItem'
import { AccountSettingsModalViewType } from './types'

interface SecurityActionsProps {
  mfaEnabled?: boolean
  hideModal: () => void
  setActiveView: (view: AccountSettingsModalViewType) => void
  config: Pick<Config, 'reCaptchaKey'>
}

export const SecurityActions = memo(
  ({
    mfaEnabled,
    hideModal: hideMainModal,
    setActiveView,
    config,
  }: SecurityActionsProps) => {
    const { pinLength } = useSecurityPin()
    const [showEditPasswordModal] = useModal(({ hideModal }) => (
      <AccountEditPasswordModal {...{ hideModal, config }} />
    ))
    const [showEditPinModal] = useModal(({ hideModal }) => (
      <AccountEditPinModal {...{ hideModal, config }} />
    ))
    const { t } = useAccountSettingsTranslation()

    const { revokeMfaHandler, isProcessingRevoke } = useMfaRevoke()

    useOnDialogBlur({
      callback: hideMainModal,
    })

    return (
      <>
        <DefaultModalContent {...{ background: 'background' }}>
          <SecurityActionItem
            {...{
              label: t('password_label'),
              description: t('password_change_description'),
              onButtonClick: showEditPasswordModal,
              buttonText: t('password_change_button_text'),
            }}
          />
          <SecurityActionItem
            {...{
              label: t('pin_label' as any, {
                length: pinLength,
              }),
              description: t('pin_change_description'),
              onButtonClick: showEditPinModal,
              buttonText: t('pin_change_button_text'),
            }}
          />
          <SecurityActionItem
            {...{
              label: t('mfaLabel'),
              description: t('mfaDescription'),
              onButtonClick: () => setActiveView('mfa_step1'),
              buttonText: t('setMfa'),
              innerContent: mfaEnabled ? (
                <MfaMethodList
                  {...{
                    mfaEnabled,
                    t,
                    onMfaRevoke: revokeMfaHandler,
                    isLoading: isProcessingRevoke,
                  }}
                />
              ) : null,
            }}
          />
        </DefaultModalContent>
        <DefaultModalButtons>
          <Button {...{ onClick: hideMainModal, variant: 'secondary' }}>
            {t('close')}
          </Button>
        </DefaultModalButtons>
      </>
    )
  },
)
